import { useCallback } from "react";

import { useShopcluesProductTypeSchemaDigestMutation } from "@/graphql/generated";

import { asRecord, slugify } from "./shopcluesSchemaDigest";

/** One normalized field card returned by the schema digest. */
export interface ShopcluesFieldCard {
  source_field_id?: string;
  source_label?: string;
  datatype?: string;
  required?: boolean;
  enums?: string[];
  path_hint?: string;
  [key: string]: unknown;
}

export interface ShopcluesIngestResult {
  marketplaceId: string;
  productType: string;
  fieldCards: ShopcluesFieldCard[];
  schemaVersion: string | null;
  /** mapping_session_id from the AI pipeline, when present. */
  mappingSessionId: string | null;
  aiResponse: unknown;
}

/** Read a File into a bare base64 string (no data-URL prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Uploads a ShopClues bulk-upload Excel template to the schema-digest mutation,
 * which forwards it to the Python parser and ingests the resulting field cards
 * into the canonical AI taxonomy pipeline. Returns the parsed field cards so the
 * UI can preview what was ingested.
 */
export function useShopcluesSchemaUpload() {
  const { mutateAsync, isPending } =
    useShopcluesProductTypeSchemaDigestMutation();

  const ingest = useCallback(
    async (params: {
      file: File;
      productType: string;
      canonicalTaxonId?: string;
      categoryId?: string;
    }): Promise<ShopcluesIngestResult> => {
      const fileBase64 = await fileToBase64(params.file);
      const canonicalTaxonId =
        params.canonicalTaxonId?.trim() ||
        `shopclues.${slugify(params.productType)}`;

      const trimmedCategoryId = params.categoryId?.trim();
      const res = await mutateAsync({
        input: {
          fileBase64,
          fileName: params.file.name,
          productType: params.productType.trim(),
          canonicalTaxonId,
          ...(trimmedCategoryId ? { categoryId: trimmedCategoryId } : {}),
        },
      });

      const digest = res.shopcluesProductTypeSchemaDigest;
      const payload = asRecord(digest.canonicalSchemaPayload);
      const fieldCards = Array.isArray(payload?.field_cards)
        ? (payload!.field_cards as ShopcluesFieldCard[])
        : [];
      const schemaVersion =
        typeof payload?.schema_version === "string"
          ? (payload.schema_version as string)
          : null;
      const ai = asRecord(digest.aiResponse);
      const mappingSessionId =
        typeof ai?.mapping_session_id === "string"
          ? (ai.mapping_session_id as string)
          : null;

      return {
        marketplaceId: digest.marketplaceId,
        productType: digest.productType,
        fieldCards,
        schemaVersion,
        mappingSessionId,
        aiResponse: digest.aiResponse,
      };
    },
    [mutateAsync],
  );

  return { ingest, isPending };
}
